import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Palette } from "lucide-react";

const STORAGE_KEY = "sug_palette";
const PALETTES = [
  { id: "mint-gold", label: "Mint Gold", swatch: "#DCAE1D" },
  { id: "sand-tan", label: "Sand Tan", swatch: "#2d545e" },
];

function readInitial() {
  if (typeof window === "undefined") return "mint-gold";
  try {
    return window.localStorage.getItem(STORAGE_KEY) || "mint-gold";
  } catch {
    return "mint-gold";
  }
}

/** Applies the active palette attribute at the <html> level. Kept here
 *  (rather than in a Context) so the CSS variables update the moment
 *  the user picks a palette without a re-render round-trip. */
function apply(paletteId) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  // Mint Gold is the DEFAULT — always emit the attribute so the CSS
  // scoped under [data-palette="mint-gold"] loads on first paint. Sand
  // Tan explicitly opts out by dropping the attribute.
  if (paletteId === "sand-tan") {
    root.removeAttribute("data-palette");
  } else {
    root.setAttribute("data-palette", paletteId);
  }
}

/**
 * PaletteToggle — cycles through available brand palettes and persists
 * the choice in localStorage. Rendered in the Header alongside the
 * language toggle. Two palettes today; extend PALETTES to add more.
 */
export function PaletteToggle({ variant = "icon", onPicked }) {
  const [palette, setPalette] = useState(readInitial);
  // Track whether the user has actually clicked once so we don't seed
  // localStorage with the default value on first paint.
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    apply(palette);
    if (!dirty) return;
    try { window.localStorage.setItem(STORAGE_KEY, palette); } catch { /* private mode */ }
  }, [palette, dirty]);

  const next = useCallback(() => {
    setDirty(true);
    const idx = PALETTES.findIndex((p) => p.id === palette);
    setPalette(PALETTES[(idx + 1) % PALETTES.length].id);
    if (onPicked) onPicked();
  }, [palette, onPicked]);

  const current = PALETTES.find((p) => p.id === palette) || PALETTES[0];

  if (variant === "row") {
    return (
      <button
        type="button"
        onClick={next}
        className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium hover:bg-accent"
        data-testid="menu-palette-toggle"
        data-palette-active={current.id}
      >
        <Palette className="h-4 w-4" />
        <span>{current.label}</span>
        <span
          aria-hidden
          className="ms-auto h-3.5 w-3.5 rounded-full border border-border"
          style={{ backgroundColor: current.swatch }}
        />
      </button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={next}
      className="rounded-xl relative"
      aria-label={`Switch palette (current: ${current.label})`}
      title={`Palette: ${current.label}`}
      data-testid="header-palette-toggle"
      data-palette-active={current.id}
    >
      <Palette className="h-5 w-5" />
      <span
        aria-hidden
        className="absolute bottom-1.5 end-1.5 h-2 w-2 rounded-full border border-background"
        style={{ backgroundColor: current.swatch }}
      />
    </Button>
  );
}

/** Initialise the palette early (before React renders) so users don't
 *  see a Sand-Tan flash when the stored choice is Mint Gold. Call from
 *  index.js right after ReactDOM is ready — or eagerly by importing. */
export function bootstrapPalette() {
  apply(readInitial());
}

// Auto-bootstrap on module import so a hard refresh applies the stored
// palette before the first paint.
bootstrapPalette();

export default PaletteToggle;
